import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { HOMEIQ_LOGO, VASTU_BY_HOMEIQ_LOGO } from "@/assets/brand";

// Landing top navigation. On desktop the six product tabs sit inline
// between the brand lockup and the "Get access" CTA. On mobile the
// tabs live in a horizontally-scrollable rail with snap points and
// left/right fade indicators so users always see that more assessments
// exist beyond the visible slice — no hamburger, no hidden architecture.
const TABS = [
    { id: "home", label: "Home" },
    { id: "affordability", label: "Affordability" },
    { id: "market", label: "Market" },
    { id: "investment", label: "Investment" },
    { id: "loan", label: "Loan" },
    { id: "vastu", label: "Vastu" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const { setActiveSection } = useHomeIQ();
    const railRef = useRef(null);
    const [edges, setEdges] = useState({ atStart: true, atEnd: false });

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Track whether the horizontal tab rail is scrolled to either edge so
    // the fade indicators can be hidden when there is nothing more to
    // reveal on that side.
    const measureEdges = useCallback(() => {
        const el = railRef.current;
        if (!el) return;
        const atStart = el.scrollLeft <= 2;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
        setEdges({ atStart, atEnd });
    }, []);

    useEffect(() => {
        measureEdges();
        const el = railRef.current;
        if (!el) return undefined;
        el.addEventListener("scroll", measureEdges, { passive: true });
        window.addEventListener("resize", measureEdges);
        return () => {
            el.removeEventListener("scroll", measureEdges);
            window.removeEventListener("resize", measureEdges);
        };
    }, [measureEdges]);

    return (
        <motion.nav
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-white/85 border-b border-[#0A1F44]/10 shadow-[0_1px_0_rgba(10,31,68,0.04)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-14 md:h-16 flex items-center justify-between gap-3 md:gap-4">
                <div className="flex items-center gap-4 shrink-0">
                    <a
                        href="#top"
                        data-testid="navbar-logo-homeiq"
                        className="flex items-center group"
                    >
                        <img
                            src={HOMEIQ_LOGO}
                            alt="HomeIQ — Home Buying Intelligence"
                            className="h-9 md:h-10 w-auto object-contain transition-transform group-hover:scale-[1.02]"
                        />
                    </a>
                    <div
                        aria-hidden="true"
                        className="hidden sm:block h-6 w-px bg-[#0A1F44]/15"
                    />
                    <a
                        href="#"
                        data-testid="navbar-logo-vastu"
                        className="hidden sm:flex items-center group"
                        aria-label="Vastu by HomeIQ"
                    >
                        <img
                            src={VASTU_BY_HOMEIQ_LOGO}
                            alt="Vastu by HomeIQ"
                            className="h-12 md:h-14 w-auto object-contain opacity-95 group-hover:opacity-100 transition-opacity"
                        />
                    </a>
                </div>

                <div className="relative flex-1 min-w-0">
                    <ul
                        ref={railRef}
                        data-testid="navbar-tab-rail"
                        className="flex items-center gap-1 lg:gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {TABS.map((t) => (
                            <li key={t.id} className="shrink-0 snap-start">
                                <button
                                    type="button"
                                    onClick={() => setActiveSection(t.id)}
                                    data-testid={`nav-tab-${t.id}`}
                                    className="px-3 py-2.5 md:py-1.5 rounded-full text-[12px] lg:text-[13px] text-[#0A1F44]/70 hover:text-[#0A1F44] hover:bg-[#0A1F44]/5 transition-colors font-medium whitespace-nowrap min-h-[40px] md:min-h-0 flex items-center"
                                >
                                    {t.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {/* Edge fades — appear only when more tabs exist beyond
                        that side. Kept below md too because desktop always
                        fits all six tabs in the rail. */}
                    <div
                        aria-hidden="true"
                        data-testid="navbar-rail-fade-left"
                        className={`md:hidden pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent transition-opacity duration-200 ${
                            edges.atStart ? "opacity-0" : "opacity-100"
                        }`}
                    />
                    <div
                        aria-hidden="true"
                        data-testid="navbar-rail-fade-right"
                        className={`md:hidden pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent transition-opacity duration-200 ${
                            edges.atEnd ? "opacity-0" : "opacity-100"
                        }`}
                    />
                </div>

                <a
                    href="#cta"
                    data-testid="navbar-cta-button"
                    className="hidden md:inline-flex group relative shrink-0 items-center gap-1.5 px-4 py-1.5 bg-[#0A1F44] text-white text-xs font-semibold rounded-full hover:bg-[#1E6BFF] transition-colors"
                >
                    <span>Get access</span>
                    <span className="inline-block transition-transform group-hover:translate-x-0.5">
                        →
                    </span>
                </a>
            </div>
        </motion.nav>
    );
}
